import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import PortfolioHero from './components/PortfolioHero';
import PortfolioFilters from './components/PortfolioFilters';
import PortfolioGrid from './components/PortfolioGrid';
import PortfolioStats from './components/PortfolioStats';
import CompanyModal from './components/CompanyModal';

const Portfolio = () => {
  const [filteredCompanies, setFilteredCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeFilters, setActiveFilters] = useState({
    industry: 'all',
    stage: 'all',
    location: 'all',
    performance: 'all'
  });

  // Mock portfolio companies data
  const portfolioCompanies = [
  {
    id: 1,
    name: "Oneplacetwostories",
    tagline: "Digital storytelling platform revolutionizing content creation",
    industry: "Media & Content",
    location: "Berlin, Germany",
    founded: "2019",
    employees: "45",
    status: "Active",
    stage: "Series A",
    investmentDate: "March 2020",
    stake: "25%",
    valuation: "€15M",
    growth: "+180%",
    featured: true,
    image: "https://images.unsplash.com/photo-1580746988097-aebeea7b2731",
    imageAlt: "Modern office space with creative professionals working on digital content at collaborative workstations",
    logo: "https://images.unsplash.com/photo-1714187199688-1e322adcec51",
    logoAlt: "Oneplacetwostories company logo featuring stylized storytelling elements",
    description: "Revolutionary platform connecting storytellers with audiences through immersive digital experiences and AI-powered content optimization.",
    fullDescription: `Oneplacetwostories has transformed the digital storytelling landscape by creating an innovative platform that seamlessly connects content creators with their audiences. The company's proprietary AI-driven technology optimizes content delivery, ensuring maximum engagement and reach across multiple digital channels.\n\nSince our investment, the platform has grown to serve over 500,000 active creators and has facilitated the creation of more than 2 million stories. The company's unique approach to narrative technology has attracted partnerships with major media companies and educational institutions across Europe.`,
    achievements: [
    "500K+ active content creators on platform",
    "2M+ stories created and published",
    "Partnership with 15+ major media companies",
    "AI content optimization technology patent filed",
    "Expansion to 12 European markets completed"],

    keyMetrics: [
    { label: "Monthly Active Users", value: "2.3M" },
    { label: "Content Pieces", value: "2M+" },
    { label: "Creator Revenue", value: "€8.5M" },
    { label: "Platform Uptime", value: "99.9%" }],

    financialMetrics: [
    { label: "Annual Revenue", value: "€12.5M" },
    { label: "Revenue Growth", value: "+180%" },
    { label: "Gross Margin", value: "78%" },
    { label: "Customer LTV", value: "€2,400" }],

    growthMetrics: [
    { label: "User Growth Rate", value: "+15% MoM" },
    { label: "Creator Retention", value: "92%" },
    { label: "Content Engagement", value: "+45%" },
    { label: "Market Expansion", value: "12 countries" }],

    founderQuote: "REXFEL's strategic guidance and operational expertise have been instrumental in scaling our platform across European markets while maintaining our core mission of empowering storytellers.",
    founderName: "Sarah Chen",
    founderTitle: "CEO & Co-Founder",
    founderAvatar: "https://images.unsplash.com/photo-1646041805292-fd77781436f9",
    founderAvatarAlt: "Professional headshot of Sarah Chen, Asian woman with shoulder-length black hair in navy blazer",
    timeline: [
    {
      date: "March 2020",
      title: "Series A Investment",
      description: "REXFEL led €8M Series A round to accelerate platform development and market expansion.",
      icon: "DollarSign",
      metrics: ["€8M raised", "25% equity", "18-month runway"]
    },
    {
      date: "August 2020",
      title: "AI Technology Launch",
      description: "Launched proprietary AI content optimization engine, increasing user engagement by 300%.",
      icon: "Cpu",
      metrics: ["+300% engagement", "Patent filed", "10 new hires"]
    },
    {
      date: "January 2021",
      title: "European Expansion",
      description: "Successfully expanded to 8 new European markets with localized content strategies.",
      icon: "Globe",
      metrics: ["8 new markets", "500K new users", "€2M ARR"]
    },
    {
      date: "June 2022",
      title: "Strategic Partnerships",
      description: "Secured partnerships with major media companies and educational institutions.",
      icon: "Handshake",
      metrics: ["15 partnerships", "€5M pipeline", "B2B revenue stream"]
    }],

    team: [
    {
      name: "Sarah Chen",
      role: "CEO & Co-Founder",
      bio: "Former VP of Product at major media company with 10+ years in digital content and storytelling platforms.",
      avatar: "https://images.unsplash.com/photo-1646041805292-fd77781436f9",
      avatarAlt: "Professional headshot of Sarah Chen, Asian woman with shoulder-length black hair in navy blazer"
    },
    {
      name: "Marcus Weber",
      role: "CTO & Co-Founder",
      bio: "AI and machine learning expert with previous experience at leading tech companies in Berlin.",
      avatar: "https://images.unsplash.com/photo-1678459915403-f06130e4018c",
      avatarAlt: "Professional headshot of Marcus Weber, Caucasian man with brown hair and beard in dark suit"
    }]

  },
  {
    id: 2,
    name: "Mobellex",
    tagline: "Smart furniture marketplace with AR visualization",
    industry: "E-commerce",
    location: "Hamburg, Germany",
    founded: "2018",
    employees: "78",
    status: "Active",
    stage: "Series B",
    investmentDate: "September 2019",
    stake: "18%",
    valuation: "€45M",
    growth: "+220%",
    featured: true,
    image: "https://images.unsplash.com/photo-1638301467457-86a6ba317bc4",
    imageAlt: "Modern furniture showroom with AR technology displays and customers using mobile devices to visualize furniture",
    logo: "https://images.unsplash.com/photo-1563892986328-e7478be5ed65",
    logoAlt: "Mobellex company logo featuring modern furniture design elements",
    description: "Next-generation furniture marketplace leveraging AR technology to revolutionize how customers discover, visualize, and purchase home furnishings.",
    fullDescription: `Mobellex has revolutionized the furniture e-commerce industry by integrating cutting-edge augmented reality technology with a comprehensive marketplace platform. The company's innovative AR visualization tools allow customers to see exactly how furniture will look in their homes before making a purchase, dramatically reducing return rates and increasing customer satisfaction.\n\nThe platform connects furniture manufacturers, designers, and retailers with consumers across Europe, facilitating over €50M in transactions annually. Mobellex's proprietary AR technology and logistics network have established it as the leading digital furniture marketplace in the German-speaking region.`,
    achievements: [
    "€50M+ in annual marketplace transactions",
    "AR visualization technology with 95% accuracy",
    "Partnership with 200+ furniture manufacturers",
    "Logistics network covering 15 European countries",
    "Customer satisfaction rating of 4.8/5 stars"],

    keyMetrics: [
    { label: "Annual GMV", value: "€50M" },
    { label: "Active Sellers", value: "200+" },
    { label: "Monthly Orders", value: "25K" },
    { label: "AR Accuracy", value: "95%" }],

    financialMetrics: [
    { label: "Annual Revenue", value: "€18.5M" },
    { label: "Revenue Growth", value: "+220%" },
    { label: "Take Rate", value: "12%" },
    { label: "Customer CAC", value: "€45" }],

    growthMetrics: [
    { label: "GMV Growth", value: "+180% YoY" },
    { label: "Seller Growth", value: "+150%" },
    { label: "Return Rate", value: "8% (vs 25% industry)" },
    { label: "Market Share", value: "15% DACH" }],

    founderQuote: "REXFEL\'s deep understanding of e-commerce and logistics has been crucial in helping us scale our AR-powered marketplace across European markets.",
    founderName: "Thomas Müller",
    founderTitle: "CEO & Founder",
    founderAvatar: "https://images.unsplash.com/photo-1549473448-b0acc73629dc",
    founderAvatarAlt: "Professional headshot of Thomas Müller, German man with blonde hair in gray suit",
    timeline: [
    {
      date: "September 2019",
      title: "Series A Investment",
      description: "REXFEL participated in €12M Series A to accelerate AR technology development.",
      icon: "DollarSign",
      metrics: ["€12M raised", "18% equity", "AR tech focus"]
    },
    {
      date: "March 2020",
      title: "AR Platform Launch",
      description: "Launched industry-leading AR visualization platform with 95% accuracy rating.",
      icon: "Smartphone",
      metrics: ["95% accuracy", "50K beta users", "Patent pending"]
    },
    {
      date: "October 2021",
      title: "Logistics Network",
      description: "Built comprehensive logistics network covering 15 European countries.",
      icon: "Truck",
      metrics: ["15 countries", "48h delivery", "€2M logistics investment"]
    },
    {
      date: "May 2023",
      title: "Series B Funding",
      description: "Raised €25M Series B to expand internationally and enhance AI capabilities.",
      icon: "TrendingUp",
      metrics: ["€25M raised", "International expansion", "AI enhancement"]
    }],

    team: [
    {
      name: "Thomas Müller",
      role: "CEO & Founder",
      bio: "Former e-commerce executive with 12+ years experience in furniture retail and marketplace development.",
      avatar: "https://images.unsplash.com/photo-1549473448-b0acc73629dc",
      avatarAlt: "Professional headshot of Thomas Müller, German man with blonde hair in gray suit"
    },
    {
      name: "Lisa Schmidt",
      role: "CTO",
      bio: "AR/VR technology specialist with background in computer vision and 3D modeling technologies.",
      avatar: "https://images.unsplash.com/photo-1734422644434-85fbdba268ee",
      avatarAlt: "Professional headshot of Lisa Schmidt, German woman with brown hair in professional attire"
    }]

  },
  {
    id: 3,
    name: "Tiksaviems",
    tagline: "AI-powered financial planning for SMEs",
    industry: "FinTech",
    location: "Munich, Germany",
    founded: "2020",
    employees: "32",
    status: "Active",
    stage: "Seed",
    investmentDate: "January 2021",
    stake: "30%",
    valuation: "€8M",
    growth: "+340%",
    featured: false,
    image: "https://images.unsplash.com/photo-1735469157670-1212e570eadc",
    imageAlt: "Modern fintech office with data analysts working on financial dashboards and AI-powered analytics systems",
    logo: "https://images.unsplash.com/photo-1678995634665-8d247af642ca",
    logoAlt: "Tiksaviems company logo featuring financial technology and AI elements",
    description: "Innovative FinTech platform providing AI-driven financial planning and cash flow optimization solutions for small and medium enterprises.",
    fullDescription: `Tiksaviems has emerged as a leading FinTech solution for SMEs, offering sophisticated AI-powered financial planning tools that were previously only available to large corporations. The platform's machine learning algorithms analyze cash flow patterns, predict financial trends, and provide actionable insights to help businesses optimize their financial performance.\n\nThe company's unique approach combines traditional financial planning with modern AI technology, making complex financial analysis accessible to businesses of all sizes. Since launch, Tiksaviems has helped over 5,000 SMEs improve their financial planning and achieve better cash flow management.`,
    achievements: [
    "5,000+ SMEs using the platform actively",
    "AI algorithms with 92% prediction accuracy",
    "€500M+ in cash flow optimized for clients",
    "Integration with 50+ banking and accounting systems",
    "Average 25% improvement in client cash flow"],

    keyMetrics: [
    { label: "Active SMEs", value: "5,000+" },
    { label: "Cash Flow Managed", value: "€500M" },
    { label: "Prediction Accuracy", value: "92%" },
    { label: "System Integrations", value: "50+" }],

    financialMetrics: [
    { label: "Annual Revenue", value: "€3.2M" },
    { label: "Revenue Growth", value: "+340%" },
    { label: "Monthly Churn", value: "2.1%" },
    { label: "Average Deal Size", value: "€640" }],

    growthMetrics: [
    { label: "Customer Growth", value: "+280% YoY" },
    { label: "Revenue per Customer", value: "+45%" },
    { label: "Market Penetration", value: "8% SME segment" },
    { label: "Product Adoption", value: "85%" }],

    founderQuote: "REXFEL\'s expertise in both technology and financial services has been invaluable in helping us build a platform that truly serves the SME market\'s unique needs.",
    founderName: "Dr. Anna Kowalski",
    founderTitle: "CEO & Co-Founder",
    founderAvatar: "https://images.unsplash.com/photo-1734456611474-13245d164868",
    founderAvatarAlt: "Professional headshot of Dr. Anna Kowalski, Polish woman with dark hair in business suit",
    timeline: [
    {
      date: "January 2021",
      title: "Seed Investment",
      description: "REXFEL led €3M seed round to develop AI-powered financial planning platform.",
      icon: "Seedling",
      metrics: ["€3M raised", "30% equity", "AI development focus"]
    },
    {
      date: "June 2021",
      title: "Platform Beta Launch",
      description: "Launched beta platform with 100 SME partners for testing and feedback.",
      icon: "Rocket",
      metrics: ["100 beta users", "AI algorithms live", "Positive feedback"]
    },
    {
      date: "December 2021",
      title: "Commercial Launch",
      description: "Full commercial launch with comprehensive SME financial planning suite.",
      icon: "Building2",
      metrics: ["1,000 customers", "€1M ARR", "92% accuracy"]
    },
    {
      date: "August 2023",
      title: "Banking Integrations",
      description: "Integrated with major European banks and accounting software providers.",
      icon: "Link",
      metrics: ["50+ integrations", "5,000 customers", "€3M ARR"]
    }],

    team: [
    {
      name: "Dr. Anna Kowalski",
      role: "CEO & Co-Founder",
      bio: "Former McKinsey consultant with PhD in Financial Mathematics and 8+ years in SME financial services.",
      avatar: "https://images.unsplash.com/photo-1734456611474-13245d164868",
      avatarAlt: "Professional headshot of Dr. Anna Kowalski, Polish woman with dark hair in business suit"
    },
    {
      name: "David Chen",
      role: "CTO & Co-Founder",
      bio: "AI and machine learning expert with previous experience at leading fintech companies in Europe.",
      avatar: "https://images.unsplash.com/photo-1610909810013-7c52994a153e",
      avatarAlt: "Professional headshot of David Chen, Asian man with short black hair in dark blazer"
    }]

  },
  {
    id: 4,
    name: "365nachrichten",
    tagline: "AI-curated news platform for German-speaking markets",
    industry: "Media & News",
    location: "Vienna, Austria",
    founded: "2019",
    employees: "55",
    status: "Active",
    stage: "Series A",
    investmentDate: "July 2020",
    stake: "22%",
    valuation: "€20M",
    growth: "+150%",
    featured: true,
    image: "https://images.unsplash.com/photo-1727857934741-93f20b9ffe71",
    imageAlt: "Modern newsroom with journalists and AI technology systems working together on digital news content",
    logo: "https://images.unsplash.com/photo-1705366664122-56ac43db9156",
    logoAlt: "365nachrichten company logo featuring news and media elements with German design aesthetics",
    description: "Leading AI-powered news aggregation and curation platform serving German-speaking markets with personalized, real-time news delivery.",
    fullDescription: `365nachrichten has transformed news consumption in German-speaking markets by leveraging advanced AI algorithms to curate, personalize, and deliver news content that matters most to each individual reader. The platform processes thousands of news sources daily, using natural language processing and machine learning to ensure users receive the most relevant and timely information.\n\nThe company has built strong partnerships with major news publishers across Germany, Austria, and Switzerland, while developing proprietary technology that enhances reader engagement and supports quality journalism through innovative revenue-sharing models.`,
    achievements: [
    "2.5M+ daily active users across DACH region",
    "AI curation technology with 94% relevance score",
    "Partnership with 150+ news publishers",
    "Average 8 minutes daily engagement per user",
    "Premium subscription conversion rate of 12%"],

    keyMetrics: [
    { label: "Daily Active Users", value: "2.5M" },
    { label: "News Sources", value: "150+" },
    { label: "AI Relevance Score", value: "94%" },
    { label: "Daily Engagement", value: "8 min" }],

    financialMetrics: [
    { label: "Annual Revenue", value: "€8.5M" },
    { label: "Revenue Growth", value: "+150%" },
    { label: "Subscription Revenue", value: "65%" },
    { label: "Publisher Revenue Share", value: "€3.2M" }],

    growthMetrics: [
    { label: "User Growth", value: "+120% YoY" },
    { label: "Subscription Growth", value: "+180%" },
    { label: "Publisher Network", value: "+200%" },
    { label: "Market Share DACH", value: "18%" }],

    founderQuote: "REXFEL\'s understanding of both media landscapes and AI technology has been crucial in helping us build a sustainable platform that serves both readers and publishers effectively.",
    founderName: "Michael Hoffmann",
    founderTitle: "CEO & Founder",
    founderAvatar: "https://images.unsplash.com/photo-1723607528434-21cde67167c4",
    founderAvatarAlt: "Professional headshot of Michael Hoffmann, Austrian man with brown hair and glasses in navy suit",
    timeline: [
    {
      date: "July 2020",
      title: "Series A Investment",
      description: "REXFEL co-led €10M Series A to enhance AI technology and expand market reach.",
      icon: "DollarSign",
      metrics: ["€10M raised", "22% equity", "AI enhancement focus"]
    },
    {
      date: "November 2020",
      title: "AI Algorithm Launch",
      description: "Deployed advanced AI curation system achieving 94% relevance scoring.",
      icon: "Brain",
      metrics: ["94% relevance", "500K users", "Publisher partnerships"]
    },
    {
      date: "April 2021",
      title: "Premium Subscription",
      description: "Launched premium subscription model with advanced personalization features.",
      icon: "Crown",
      metrics: ["12% conversion", "€2M ARR", "Premium features"]
    },
    {
      date: "September 2022",
      title: "Publisher Network",
      description: "Expanded publisher network to 150+ sources with revenue-sharing model.",
      icon: "Network",
      metrics: ["150+ publishers", "€3.2M shared", "2.5M DAU"]
    }],

    team: [
    {
      name: "Michael Hoffmann",
      role: "CEO & Founder",
      bio: "Former journalist and media executive with 15+ years experience in digital news and content platforms.",
      avatar: "https://images.unsplash.com/photo-1723607528434-21cde67167c4",
      avatarAlt: "Professional headshot of Michael Hoffmann, Austrian man with brown hair and glasses in navy suit"
    },
    {
      name: "Elena Richter",
      role: "CTO",
      bio: "AI and NLP specialist with background in computational linguistics and news technology systems.",
      avatar: "https://images.unsplash.com/photo-1684262855358-88f296a2cfc2",
      avatarAlt: "Professional headshot of Elena Richter, German woman with blonde hair in professional blazer"
    }]

  }];


  // Filter companies based on active filters
  useEffect(() => {
    setLoading(true);

    setTimeout(() => {
      let filtered = portfolioCompanies;

      if (activeFilters?.industry !== 'all') {
        const industryMap = {
          'ecommerce': ['E-commerce'],
          'saas': ['SaaS'],
          'fintech': ['FinTech'],
          'media': ['Media & News', 'Media & Content'],
          'marketplace': ['E-commerce']
        };

        const targetIndustries = industryMap?.[activeFilters?.industry] || [];
        filtered = filtered?.filter((company) =>
        targetIndustries?.some((industry) => company?.industry?.includes(industry))
        );
      }

      if (activeFilters?.stage !== 'all') {
        filtered = filtered?.filter((company) =>
        company?.stage?.toLowerCase()?.includes(activeFilters?.stage?.replace('-', ' '))
        );
      }

      if (activeFilters?.location !== 'all') {
        const locationMap = {
          'germany': ['Berlin', 'Hamburg', 'Munich'],
          'europe': ['Vienna'],
          'north-america': [],
          'global': []
        };

        const targetLocations = locationMap?.[activeFilters?.location] || [];
        if (targetLocations?.length > 0) {
          filtered = filtered?.filter((company) =>
          targetLocations?.some((location) => company?.location?.includes(location))
          );
        }
      }

      if (activeFilters?.performance !== 'all') {
        if (activeFilters?.performance === 'high-growth') {
          filtered = filtered?.filter((company) =>
          parseInt(company?.growth?.replace('%', '')?.replace('+', '')) >= 150
          );
        } else if (activeFilters?.performance === 'profitable') {
          filtered = filtered?.filter((company) => company?.status === 'Active');
        } else if (activeFilters?.performance === 'unicorn') {
          filtered = filtered?.filter((company) =>
          parseInt(company?.valuation?.replace('€', '')?.replace('M', '')) >= 20
          );
        }
      }

      setFilteredCompanies(filtered);
      setLoading(false);
    }, 800);
  }, [activeFilters]);

  const handleFilterChange = (category, value) => {
    setActiveFilters((prev) => ({
      ...prev,
      [category]: value
    }));
  };

  const handleViewDetails = (company) => {
    setSelectedCompany(company);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedCompany(null);
  };

  // Scroll reveal effect
  useEffect(() => {
    const observerCallback = (entries) => {
      entries?.forEach((entry) => {
        if (entry?.isIntersecting) {
          entry?.target?.classList?.add('revealed');
        }
      });
    };

    const observer = new IntersectionObserver(observerCallback, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });

    const elements = document.querySelectorAll('.scroll-reveal');
    elements?.forEach((el) => observer?.observe(el));

    return () => observer?.disconnect();
  }, [filteredCompanies]);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>Portfolio - REXFEL Investment Platform | Strategic Investment Partners</title>
        <meta name="description" content="Explore REXFEL's portfolio of innovative companies transforming industries through strategic investment, operational expertise, and digital-first thinking. Discover our success stories and investment approach." />
        <meta name="keywords" content="investment portfolio, venture capital, startup investments, REXFEL portfolio companies, strategic partnerships, European investments" />
        <meta property="og:title" content="Portfolio - REXFEL Investment Platform" />
        <meta property="og:description" content="Discover our portfolio of innovative companies transforming industries through strategic investment and operational expertise." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://rexfel.com/portfolio" />
      </Helmet>

      <Header />

      <main className="pt-16">
        <PortfolioHero />
        <PortfolioFilters
          onFilterChange={handleFilterChange}
          activeFilters={activeFilters} />

        <PortfolioGrid
          companies={filteredCompanies}
          onViewDetails={handleViewDetails}
          loading={loading} />

        <PortfolioStats />
      </main>

      <CompanyModal
        company={selectedCompany}
        isOpen={isModalOpen}
        onClose={handleCloseModal} />

      {/* Footer */}
      <footer className="bg-card border-t border-border py-12">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8">
            {/* Company Info */}
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 rounded-brand-sm bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                  <span className="text-white font-bold text-lg font-mono">R</span>
                </div>
                <div>
                  <h3 className="font-bold text-foreground">REXFEL</h3>
                  <p className="text-xs text-muted-foreground">Technology and Innovation</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground">
                Strategic investment meets digital innovation. Empowering European startups to scale globally.
              </p>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="font-medium text-foreground mb-4">Platform</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="/portfolio" className="hover:text-primary transition-brand">Portfolio</a></li>
                <li><a href="/crypto-dashboard" className="hover:text-primary transition-brand">Crypto Dashboard</a></li>
                <li><a href="/news" className="hover:text-primary transition-brand">Market Insights</a></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h4 className="font-medium text-foreground mb-4">Company</h4>
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li><a href="/about" className="hover:text-primary transition-brand">About Us</a></li>
                <li><a href="/contact" className="hover:text-primary transition-brand">Contact</a></li>
                <li><a href="/legal-hub" className="hover:text-primary transition-brand">Legal Hub</a></li>
              </ul>
            </div>

            {/* Contact Info */}
            <div>
              <h4 className="font-medium text-foreground mb-4">Hamburg Office</h4>
              <div className="space-y-2 text-sm text-muted-foreground">
                <p>Wandsbek District</p>
                <p>22119 Hamburg, Germany</p>
                <p>+49 172 7999621</p>
                <p>info@rexfel.com</p>
              </div>
            </div>
          </div>

          {/* Bottom Bar */}
          <div className="border-t border-border mt-8 pt-8 flex flex-col md:flex-row justify-between items-center">
            <p className="text-sm text-muted-foreground">
              © {new Date()?.getFullYear()} REXFEL Technology and Innovation. All rights reserved.
            </p>
            <div className="flex items-center space-x-6 mt-4 md:mt-0">
              <span className="text-xs text-muted-foreground">Hamburg Chamber of Commerce</span>
              <span className="text-xs text-muted-foreground">EU Regulated</span>
              <span className="text-xs text-muted-foreground">GDPR Compliant</span>
            </div>
          </div>
        </div>
      </footer>

    </div>);

};

export default Portfolio;